import { Offer } from "@/types/offer";
import { getObjectDiff } from "./versionDiff";
import { Timestamp, GeoPoint, DocumentReference } from "firebase/firestore";

// Fields that should not be stored if they are null/empty
const omittableFields = [
  "campExperienceData.themeOtherKeys",
  "currentSalePrice",
  "locationData.photoTeamCaption",
  "locationData.photoTeamPath",
  "numDaysOverride",
  "priceNoteOverride",
  "priceOverride",
  "timingNoteOverride",
  "variationData.daysOfWeek",
  "variationData.description",
  "variationData.maxAgeGradeKey",
  "variationData.minAgeGradeKey",
  "variationData.numWeeks",
  "variationData.timings.afterCareEnd",
  "variationData.timings.afterCareStart",
  "variationData.timings.beforeCareEnd",
  "variationData.timings.beforeCareStart",
  "variationData.timings.timingNote"
];

// Critical fields that should NEVER be omitted
const criticalFields = [
  "startDate",
  "endDate",
  "locationData.descAboutTheOrg",
  "locationData.descTimingAndBooking",
  "locationData.religiousAffiliationKeys",
  "locationData.supportForKeys",
  "locationData.allergyKeys",
  "locationData.photoPath",
  "locationData.photoYOffset",
  "lastEditedBy"
];

// Check if a field is omittable and its value is null/empty
export const shouldOmitField = (path: string, value: any): boolean => {
  // Never omit critical fields regardless of value
  if (criticalFields.some(field => path === field || path.startsWith(field + '.'))) {
    return false;
  }
  
  // Check if it's in the omittable list
  if (!omittableFields.includes(path)) return false;
  
  // Check for null/undefined
  if (value === null || value === undefined) return true;
  
  // Check for empty strings
  if (typeof value === "string" && value.trim() === "") return true;
  
  // Check for empty arrays
  if (Array.isArray(value) && value.length === 0) return true;
  
  return false;
};

// Format values for human-readable display in diff
const formatDiffValue = (value: any): string => {
  if (value === undefined || value === null) {
    return 'null';
  }
  
  // Handle Date objects first
  if (value instanceof Date) {
    return value.toLocaleString();
  }
  
  // Format Firebase Timestamps
  if (value instanceof Timestamp) {
    return value.toDate().toLocaleString();
  }
  
  // Handle ISO date strings (potentially from prepareOfferForDiff)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
    try {
      return new Date(value).toLocaleString();
    } catch (e) {
      // Fall through if parsing fails, treat as regular string
    }
  }
  
  if (value instanceof GeoPoint) {
    return `${value.latitude}, ${value.longitude}`;
  }
  
  if (value instanceof DocumentReference) {
    return value.path;
  }
  
  // Format arrays
  if (Array.isArray(value)) {
    return `[${value.map(v => formatDiffValue(v)).join(', ')}]`;
  }
  
  // Format objects
  if (typeof value === 'object' && value !== null) {
    // For regEvents, provide a more readable summary
    if (Array.isArray(value) && value.every(item => typeof item === 'object' && item !== null && 'eventKey' in item && 'when' in item)) {
      const eventSummaries = value.map(event => {
        const eventKey = event.eventKey || 'N/A';
        const eventTime = event.when ? new Date(event.when).toLocaleString() : 'No Date';
        return `${eventKey} (${eventTime})`;
      });
      return `[${eventSummaries.join(', ')}]`;
    }
    return JSON.stringify(value);
  }
  
  return String(value);
};

// Prepare an offer for diffing by handling special types
export const prepareOfferForDiff = (offer: any): any => {
  if (offer === null || offer === undefined) {
    return offer;
  }
  
  // Handle non-objects
  if (typeof offer !== 'object') {
    return offer;
  }
  
  // Clone to avoid modifying original
  const result: any = Array.isArray(offer) ? [] : {};
  
  // Process each key
  Object.entries(offer).forEach(([key, value]: [string, any]) => {
    // Skip internal keys
    if (key.startsWith('_') || key === 'id') {
      result[key] = value;
      return;
    }
    
    // Handle Date objects (convert to ISO string for consistent diffing)
    if (value instanceof Date) {
      result[key] = value.toISOString();
      return;
    }
    
    // Handle Firebase Timestamps (convert to ISO string)
    if (value instanceof Timestamp) {
      result[key] = value.toDate().toISOString();
      return;
    }
    
    // Handle Firebase Timestamps from unknown objects with proper type checking
    if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
      try {
        const timestamp = value as { toDate: () => Date };
        result[key] = timestamp.toDate().toISOString();
        return;
      } catch (e) {
        // Fall through to handle as regular object
      }
    }
    
    if (value instanceof GeoPoint) {
      result[key] = `${value.latitude},${value.longitude}`;
      return;
    }
    
    // Handle GeoPoint from unknown objects with proper type checking
    if (value && typeof value === 'object' && 'latitude' in value && 'longitude' in value) {
      const obj = value as { latitude: unknown; longitude: unknown };
      if (typeof obj.latitude === 'number' && typeof obj.longitude === 'number') {
        result[key] = `${obj.latitude},${obj.longitude}`;
        return;
      }
    }
    
    if (value instanceof DocumentReference) {
      result[key] = value.path;
      return;
    }
    
    // Handle DocumentReference from unknown objects with proper type checking
    if (value && typeof value === 'object' && 'path' in value) {
      const obj = value as { path: unknown };
      if (typeof obj.path === 'string') {
        result[key] = obj.path;
        return;
      }
    }
    
    // Recursively handle objects and arrays
    if (value !== null && typeof value === 'object') {
      result[key] = prepareOfferForDiff(value);
      return;
    }
    
    // Keep other values as is
    result[key] = value;
  });
  
  return result;
};

// KB-2145 — top-level offer fields the admin preparer does not own and never emits. The Review
// Changes dialog diffs the STORED offer against `prepareOfferData`'s output over the UNION of their
// keys, so a pipeline-written field that the preparer omits shows up as a removal row
// (`{...} → null`). That row is a phantom: the admin save is an `updateDoc` with a map that simply
// lacks the key, and updateDoc does not delete absent keys — nothing is removed. Without this
// filter an operator editing any pipeline-created session sees "observedBands will be deleted",
// which is both alarming and false.
// KB-2750 — `daysOfWeekOverride` joins `observedBands` for the same reason: the pipeline writes it
// at CREATE and `prepareOfferData` never emits it, so `getObjectDiff`'s key-union walk renders a
// phantom `"MTWRF" → null` deletion row in Review Changes for every pipeline-created offer. Nothing
// is actually erased — the preparer-driven rewrite paths omit the key rather than clearing it — but
// an operator would see a deletion that is not happening.
// KB-2885 — `signupObserved` joins them for the third time, same mechanism: the pipeline seeds it at
// CREATE to record whether an offer's `signupStatus` was stated by the vendor, not observed, or is
// simply unknown (since KB-4074 signup is never derived from availability prose: the registration
// window and capacity are orthogonal). `prepareOfferData` never emits it, so without this
// filter every pipeline-created session would show an operator a phantom `false → null` deletion row.
//
// KB-2877 (review round 3, blocking 1) — `numDaysState` is deliberately NOT in this set, and that is
// the difference between it and the three above: `prepareOfferData` DOES emit it
// (`offerDataPreparation.ts`), so both sides of `generateOfferDiff` carry the key and there is no
// phantom row to suppress. Adding it would HIDE A REAL EDIT — a curator who sets `numDaysOverride`
// on a withheld offer clears the state, and that genuine `"withheld" → null` row would vanish from
// Review Changes, which is the silently-hidden-edit hazard `offerDiffUtils.test.ts`'s KB-2809
// invariant exists to catch. It did catch it: round 2 added the entry on a justification the same
// commit falsified, and the required Root Tests gate went red on
// `extraAndNotVolatile: ["numDaysState"]`. `VOLATILE_OFFER_FIELDS` + `DIFF_IGNORE_FIELDS` is not the
// alternative either — the field must stay diffed and appliable or the withhold never reaches the
// live rows. **Do not re-add it here.**
// KB-3769 — `priceSource` ("program-default") is the fourth: seeded at CREATE by the camp-data builder,
// never emitted by `prepareOfferData`. KB-4220 — `programUrl` and `infoLink` (the session's program page
// and which page describes it) are the fifth and sixth, for the same reason. KB-4103 — `suStatusAsOf`
// (the registration window's freshness stamp, written by the camp-data writer on a `signupStatus` write)
// is never emitted by `prepareOfferData` either, nor is `availability` (the Algolia facet the
// `onOfferWrittenUpdateAvailabilityFacet` trigger derives from `availabilityStatus`, populated since KB-4103),
// nor `lastKnownAvailabilityStatus` (KB-4103 Part B: the capacity reading an `av_unknown` replaced).
const PIPELINE_OWNED_TOP_LEVEL_FIELDS = new Set([
  "observedBands",
  // KB-4535 — the window the source stated, stamped at create beside observedBands; never emitted by prepareOfferData.
  "observedWindow",
  "daysOfWeekOverride",
  "signupObserved",
  "priceSource",
  "programUrl",
  "infoLink",
  "programUrlSource",
  "suStatusAsOf",
  "availability",
  "lastKnownAvailabilityStatus",
]);

// KB-4346 — provenance stamps a save writes alongside a field it changes. Never a row of its own: the
// change it records already shows as the field's row (`programUrl`).
const PROVENANCE_ONLY_TOP_LEVEL_FIELDS = new Set(["programUrlSource"]);

// Generate a diff between two offers
export const generateOfferDiff = (currentOffer: any, newOfferData: any) => {
  const preparedCurrent = prepareOfferForDiff(currentOffer);
  const preparedNew = prepareOfferForDiff(newOfferData);

  // A pipeline-owned field is hidden only when the save does not carry it — the phantom "will be
  // deleted" row. KB-4351: the offer-prep seams now copy the program's `programUrl`, and a value the
  // save really writes must show.
  const carries = (field: string) =>
    newOfferData != null && Object.prototype.hasOwnProperty.call(newOfferData, field);
  const diff = getObjectDiff(preparedCurrent, preparedNew).filter((change: { path: string }) => {
    const top = String(change.path).split('.')[0];
    if (PROVENANCE_ONLY_TOP_LEVEL_FIELDS.has(top)) return false;
    return !PIPELINE_OWNED_TOP_LEVEL_FIELDS.has(top) || carries(top);
  });

  // Enhance diff with formatted values and omission information
  return diff.map(change => {
    const willBeOmitted = shouldOmitField(change.path, change.newValue);
    
    return {
      ...change,
      formattedOldValue: formatDiffValue(change.oldValue),
      formattedNewValue: willBeOmitted 
        ? "null (won't store the field)" 
        : formatDiffValue(change.newValue),
      willBeOmitted
    };
  });
};

// Checks if user has admin role
export const isAdminUser = (userRoles?: string[]): boolean => {
  if (!userRoles || userRoles.length === 0) {
    return false;
  }
  
  return userRoles.includes('admin_user') || userRoles.includes('admin');
};
